import React, { useState, useMemo, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { RelPoint, RelSide, RelType } from 'src/config';
import * as types from '../../types';
import * as actions from '../../actions';
import { Box, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import YoutubeSearchedForIcon from '@mui/icons-material/YoutubeSearchedFor';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import Lookup from './Lookup';
import IconButton from '../components/IconButton';
import RelContainer from './components/RelContainer';
import { Scope as ExplorerScope, ExplorerContext } from '../../components/Entity';
import { getPrincipal, AccessLevel, Principal } from 'src/auth';

const RelatedMany: React.FC<{
    parentLocator: types.Locator,
    vector: RelPoint;
    relEntities?: types.Entity[], // related entities (either direct or inverse).
    onLink: (ids: types.ID[]) => void,
    onUnLink: (ids: types.ID[]) => void,
    onDelete: (ids: types.ID[]) => void,
    stack: types.StackItem[];
    entity?: types.Entity; // main entity (either owner or being pointed at by related - inverse).
}> = ({
    parentLocator,
    vector,
    relEntities,
    onLink,
    onUnLink,
    onDelete,
    stack,
    entity,
}) => {
        const dispatch = useDispatch();
        const principal: Principal = getPrincipal();

        // main entity locked
        const isLocked: boolean = entity?.locked || false;

        const { side: dir, rel } = vector;
        const inverse: boolean = (dir === RelSide.TARGET);
        const [lookup, setLookup] = useState<{ local: boolean } | undefined>();
        const resourceName = useMemo(() => inverse ? rel.resource : rel.related, [rel, inverse]);
        const userCanManage: boolean = principal.hasAccessResource(resourceName, AccessLevel.Manage);
        const attrName = useMemo(() => inverse ? rel.backReferenceName : rel.referenceName, [rel, inverse]);
        const lockedAreNonSelectable: boolean = (rel.metaType == RelType.MANY_TO_MANY && inverse) || (rel.metaType == RelType.MANY_TO_ONE && inverse);


        const getLocator: (relEntity: types.Entity) => types.Locator | undefined = (relEntity) => {
            return relEntity ? {
                name: inverse ? rel.resource : rel.related,
                id: (relEntity.attrs['_id'] ?? relEntity.values['_id']).toString(),
            } : undefined;
        }

        const editAction = (locator?: types.Locator) => locator ? <IconButton onClick={() => dispatch(actions.pushAction(locator!, vector))}>
            <EditIcon fontSize='small' />
        </IconButton> : null;

        const createAction = <IconButton onClick={() => {
            const port: types.RelPort = {
                relPoint: vector,
                locator: parentLocator,
                attrName,
            }
            dispatch(actions.createTemp(port));
        }}>
            <AddIcon fontSize='small' />
        </IconButton>;

        const unlinkAction = (locator?: types.Locator) => locator ? <IconButton onClick={() => { onUnLink([locator.id as types.ID]) }}>
            <LinkOffIcon fontSize='small' />
        </IconButton> : null;

        const deleteAction = (locator?: types.Locator) => locator ? <IconButton onClick={() => { onDelete([locator.id as types.ID]) }}>
            <DeleteIcon fontSize='small' />
        </IconButton > : null;

        const searchAction = <IconButton onClick={() => setLookup({ local: false })}>
            <SearchIcon fontSize='small' />
        </IconButton>;

        const searchLocalAction = <IconButton onClick={() => setLookup({ local: true })}>
            <YoutubeSearchedForIcon fontSize='small' />
        </IconButton>;

        const renderActions = (entity: types.Entity) => {
            const locator = getLocator(entity);
            const locked: boolean = entity?.locked || false;
            return userCanManage ? <>
                {
                    editAction(locator)
                }
                {
                    (inverse && locked) || (!inverse && isLocked) ? null : unlinkAction(locator)
                }
                {
                    deleteAction(locator)
                }
            </> : <></>;
        }

        return (
            <Box sx={{ mt: 1 }}>
                <Typography sx={{ mb: 1, fontWeight: 'bold' }}>{inverse ? (rel.backReferenceLabel || rel.backReferenceName) : (rel.referenceLabel || rel.referenceName)}:</Typography>

                <RelContainer
                    vector={vector}
                    stack={stack}
                    entities={relEntities}
                    parentLocator={parentLocator}
                    renderItemActions={renderActions}
                />

                <Box
                    sx={{
                        minHeight: '3em',
                        pt: 1,
                        display: 'flex',
                        flexDirection: 'row-reverse',
                    }}
                >
                    {
                        userCanManage && searchLocalAction
                    }
                    {
                        userCanManage && searchAction
                    }
                    {
                        userCanManage && createAction
                    }
                </Box>

                <Lookup
                    rel={rel}
                    inverse={inverse}
                    open={lookup != null}
                    local={lookup?.local || false}
                    onClose={() => {
                        setLookup(undefined);
                    }}
                    onSelect={(ids: types.ID[]) => onLink(ids)}
                    lockedAreNonSelectable={lockedAreNonSelectable}
                />
            </Box>
        )
    }

export default RelatedMany;